import {
  createContext,
  useState,
  useMemo,
  useContext,
  useEffect,
  type JSX,
} from 'react';
import { AddressType } from 'entities/address-types';
import { apiPurchasePreview } from 'lib/api';
import { trackEvent } from 'lib/analytics';
import {
  getSubscriptionItems,
  sanitizeAddress,
  getMostRecentlyUsedAddress,
  getCheckoutId,
} from 'components/pages/checkout/checkout-helpers';
import { PaymentType } from 'entities/payment-types';
import {
  PreviewOrderData,
  PurchasePreviewInfo,
  PurchasePreviewInfoProps,
} from 'components/pages/checkout/entities';
import { useRouter } from 'next/router';
import { getErrorMessage } from 'lib/errors';
import { PaymentFormErrors } from 'components/pages/checkout/entities';
import { AddressFormErrors } from 'components/reusable/address-form/entities';
import { CartContext } from './cart-provider';
import { UserContext } from './user-provider';
import { getPersistedPromoCode, removePersistedPromoCode } from 'lib/promos';

// ---------------- Setup ----------------
const emptyBillingAddress = {
  firstName: '',
  lastName: '',
  street1: '',
  street2: '',
  city: '',
  region: '',
  postalCode: '',
  country: 'US',
} as AddressType;

const emptyShippingAddress = {
  ...emptyBillingAddress,
  company: '',
  phone: '',
  address_id: '',
} as AddressType;

const emptyPurchasePreviewInfo = {
  previewTotalPrice: 0,
  previewSubTotalPrice: 0,
  previewCreditsApplied: 0,
  previewDiscountPrice: 0,
  shippingCosts: 0,
  taxes: 0,
} as PurchasePreviewInfo;

export type CheckoutProps = {
  children: JSX.Element;
};

export const initialContext = {
  checkoutStep: 0,
  setCheckoutStep: (_checkoutStep: number) => null,

  shippingAddress: emptyShippingAddress as AddressType,
  setShippingAddress: (_address: AddressType) => null,
  clearShippingAddress: () => null,
  isUsingNewShippingAddress: true,
  setIsUsingNewShippingAddress: (_isUsingNewShippingAddress: boolean) => null,
  isEditingShippingAddress: false,
  setIsEditingShippingAddress: (_isEditingShippingAddress: boolean) => null,

  expeditedShippingCost: 0,
  setExpeditedShippingCost: (_number) => null,

  billingAddress: emptyBillingAddress as AddressType,
  setBillingAddress: (_address: AddressType) => null,

  useShippingAsBillingAddress: true,
  setUseShippingAsBillingAddress: (_useShippingAsBillingAddress: boolean) =>
    null,

  paymentType: 'cc' as PaymentType,
  setPaymentType: (_paymentType: PaymentType) => null,

  paymentToken: '' as string,
  setPaymentToken: (_paymentToken: string) => null,

  isUsingExistingPaymentMethod: false,
  setisUsingExistingPaymentMethod: (_useExisting: boolean) => null,

  hasAcceptedTerms: false,
  setHasAcceptedTerms: (_terms: boolean) => null,

  hasOptedInToEmail: true,
  setHasOptedInToEmail: (_hasOptedInToEmail: boolean) => null,

  isSmsOptedIn: false,
  setIsSmsOptedIn: (_isSmsOptedIn: boolean) => null,

  purchasePreviewInfo: {
    previewTotalPrice: 0,
    previewSubTotalPrice: 0,
    previewCreditsApplied: 0,
    previewDiscountPrice: 0,
    shippingRates: 0,
    currency: 'USD',
  } as PurchasePreviewInfo,

  setPurchasePreviewInfo: (_purchasePreviewInfo: PurchasePreviewInfo) => null,
  getAndStorePurchasePreviewInfo: async (_props: PurchasePreviewInfoProps) =>
    ({}) as Promise<{ data?: any; status?: number }>,

  trackCouponApplied: (
    _couponCode: string,
    _discount: number,
    _entryMethod?: string,
    _couponType?: 'fixed' | 'percent',
  ) => null,
  trackCouponDenied: (_couponCode: string, _reason: string) => null,
  trackCouponFraud: (_couponCode: string, _reason: string) => null,
  trackCouponRemoved: (_couponCode: string) => null,

  userIsVerified: false,
  setUserIsVerified: (_isVerified: boolean) => null,

  shippingAddressErrors: [] as string[],
  setShippingAddressErrors: (_errors: string[]) => null,
  billingAddressErrors: [] as string[],
  setBillingAddressErrors: (_errors: string[]) => null,
  paymentErrors: [] as string[],
  setPaymentErrors: (_errors: string[]) => null,
  termsError: [] as string[],
  setTermsError: (_errors: string[]) => null,
  purchasePreviewErrors: [] as string[],
  setPurchasePreviewErrors: (_errors: string[]) => null,
  submitErrors: [] as string[],
  setSubmitErrors: (_errors: string[]) => null,
  applePayErrors: [] as string[],
  setApplePayErrors: (_errors: string[]) => null,
  turnstileErrors: [] as string[],
  setTurnstileErrors: (_errors: string[]) => null,
  clearAllErrors: () => null,

  // --------------- V3 Form Errors ---------------
  shippingAddressFormErrors: {} as AddressFormErrors,
  setShippingAddressFormErrors: (_errors: AddressFormErrors) => null,
  billingAddressFormErrors: {} as AddressFormErrors,
  setBillingAddressFormErrors: ({}: AddressFormErrors) => null,
  paymentFormErrors: {} as PaymentFormErrors,
  setPaymentFormErrors: (_errors: PaymentFormErrors) => null,
  // --------------- V3 Form Errors ---------------
};

export type CheckoutContextType = typeof initialContext;

export const CheckoutContext =
  createContext<CheckoutContextType>(initialContext);
// ---------------- Setup ----------------

// ---------------- Provider ----------------
export const CheckoutProvider: React.FC<CheckoutProps> = ({
  children,
}: CheckoutProps) => {
  const router = useRouter();

  // ---------------- Contexts ----------------
  const { userCart, promoCode } = useContext(CartContext);
  const { userState } = useContext(UserContext);
  // ---------------- Contexts ----------------

  // ---------------- Miscellaneous State----------------
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [hasOptedInToEmail, setHasOptedInToEmail] = useState(true);
  const [isSmsOptedIn, setIsSmsOptedIn] = useState(false);

  const [userIsVerified, setUserIsVerified] = useState(false);
  // ---------------- Miscellaneous State----------------

  // --------------- V3 Form Errors ---------------
  const [shippingAddressFormErrors, setShippingAddressFormErrors] = useState(
    {} as AddressFormErrors,
  );
  const [billingAddressFormErrors, setBillingAddressFormErrors] = useState(
    {} as AddressFormErrors,
  );
  const [paymentFormErrors, setPaymentFormErrors] = useState<PaymentFormErrors>(
    {} as PaymentFormErrors,
  );
  // --------------- V3 Form Errors ---------------

  // ---------------- Errors ----------------
  const [purchasePreviewErrors, setPurchasePreviewErrors] = useState<string[]>(
    [],
  );
  const [termsError, setTermsError] = useState([]);
  const [applePayErrors, setApplePayErrors] = useState([] as string[]);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [turnstileErrors, setTurnstileErrors] = useState([]);
  const [shippingAddressErrors, setShippingAddressErrors] = useState([]);
  const [billingAddressErrors, setBillingAddressErrors] = useState([]);
  const [paymentErrors, setPaymentErrors] = useState([]);
  const clearAllErrors = () => {
    setPaymentErrors([]);
    setTermsError([]);
    // V3 errors
    setApplePayErrors([]);
    setSubmitErrors([]);
    setShippingAddressErrors([]);
    setBillingAddressErrors([]);
    setShippingAddressFormErrors({} as AddressFormErrors);
    setPaymentFormErrors({} as PaymentFormErrors);
  };

  // ---------------- Errors ----------------

  // ----------- Life Cycle Methods ------------
  useEffect(() => {
    if (router.isReady) {
      // Only restore from localStorage on the checkout page
      // Product pages will fetch fresh data
      if (router.pathname === '/checkout') {
        const persistedPurcasePreviewInfo = JSON.parse(
          localStorage.getItem('purchasePreviewInfo') || '{}',
        );
        setPurchasePreviewInfo(
          persistedPurcasePreviewInfo || emptyPurchasePreviewInfo,
        );
      } else {
        setPurchasePreviewInfoInt(emptyPurchasePreviewInfo);
      }
    }
  }, [router.isReady, router.pathname]);

  useEffect(() => {
    const loggedInUserShippingAddress = getMostRecentlyUsedAddress(userState);
    if (loggedInUserShippingAddress && !isEditingShippingAddress) {
      setShippingAddressInternal(sanitizeAddress(loggedInUserShippingAddress));
      setIsUsingNewShippingAddress(false);
    }
  }, [userState]);
  // ----------- Life Cycle Methods ------------

  // ---------------- ShippingInfo ----------------
  const [shippingAddress, setShippingAddressInternal] =
    useState<AddressType>(emptyShippingAddress);
  const [isEditingShippingAddress, setIsEditingShippingAddress] = useState(
    !userState?.account?.details?.shipping_addresses?.[0],
  );
  const [isUsingNewShippingAddress, setIsUsingNewShippingAddress] =
    useState(true);

  useEffect(() => {
    let defaultBillingAddress = emptyBillingAddress;
    const loggedInUserBillingAdrress =
      userState?.account?.details?.billing_info?.address;

    if (
      loggedInUserBillingAdrress?.postal_code ||
      loggedInUserBillingAdrress?.postalCode
    ) {
      defaultBillingAddress = loggedInUserBillingAdrress;
    } else if (shippingAddress?.postalCode || shippingAddress?.postal_code) {
      defaultBillingAddress = shippingAddress;
    }

    if (defaultBillingAddress) {
      setBillingAddress(defaultBillingAddress);
    }
  }, [userState]);

  const setShippingAddress = (address: AddressType) => {
    const sanitized = sanitizeAddress(address);
    setShippingAddressInternal(sanitizeAddress(sanitized));
  };

  const clearShippingAddress = () => {
    setShippingAddressInternal(emptyShippingAddress);
  };

  // ---------------- BillingAddress ----------------

  const [billingAddress, setBillingAddressInt] = useState(emptyBillingAddress);
  const [useShippingAsBillingAddress, setUseShippingAsBillingAddressInt] =
    useState(true);

  const setBillingAddress = (billingAdd: AddressType) => {
    setBillingAddressInt(sanitizeAddress(billingAdd));
  };
  const [expeditedShippingCost, setExpeditedShippingCost] = useState(0); // 0 or 4.99

  const setUseShippingAsBillingAddress = (val) => {
    setUseShippingAsBillingAddressInt(val);
  };

  // ---------------- BillingAddress ----------------

  // ---------------- PaymentInfo ----------------
  const [paymentToken, setPaymentToken] = useState('' as string);
  const [paymentType, setPaymentTypeInt] = useState('cc' as PaymentType);
  const [isUsingExistingPaymentMethod, setisUsingExistingPaymentMethod] =
    useState(false);

  const setPaymentType = (type: PaymentType) => {
    setPaymentErrors([]);
    setPaymentFormErrors({} as PaymentFormErrors);
    setPaymentTypeInt(type);
  };
  // ---------------- PaymentInfo ----------------

  // ---------------- Terms ----------------
  const [hasAcceptedTerms, setHasAcceptedTermsInt] = useState(false);
  const setHasAcceptedTerms = (terms: boolean) => {
    setHasAcceptedTermsInt(terms);
    if (terms) {
      setTermsError([]);
    }
  };
  // ---------------- Terms ----------------

  // Add centralized coupon analytics tracking methods
  const trackCouponApplied = (
    couponCode: string,
    discount: number,
    entryMethod?: string,
    type?: 'fixed' | 'percent',
  ) => {
    trackEvent('Coupon Applied', {
      cart_id: userCart.cartId(),
      coupon_id: couponCode,
      coupon_name: couponCode,
      discount: Number(discount),
      coupon_type: type,
      coupon_entry_method: entryMethod || 'manual',
    });
  };

  const trackCouponDenied = (
    couponCode: string,
    reason: string,
    entryMethod?: string,
  ) => {
    trackEvent('Coupon Denied', {
      cart_id: userCart.cartId(),
      coupon_id: couponCode,
      coupon_name: couponCode,
      reason: reason,
      coupon_entry_method: entryMethod || 'manual',
    });
  };

  const trackCouponFraud = (
    couponCode: string,
    reason: string,
    entryMethod?: string,
  ) => {
    trackEvent('Coupon Fraud', {
      cart_id: userCart.cartId(),
      coupon_id: couponCode,
      coupon_name: couponCode,
      reason: reason,
      coupon_entry_method: entryMethod || 'manual',
    });
  };

  const trackCouponRemoved = (couponCode: string) => {
    trackEvent('Coupon Removed', {
      cart_id: userCart.cartId(),
      code_id: couponCode,
      code_name: couponCode,
    });
  };

  // ---------------- PurchasePreviewInfo ----------------
  const [purchasePreviewInfo, setPurchasePreviewInfoInt] =
    useState<PurchasePreviewInfo>(emptyPurchasePreviewInfo);
  const setPurchasePreviewInfo = (data: PurchasePreviewInfo) => {
    setPurchasePreviewInfoInt(data);
    localStorage.setItem('purchasePreviewInfo', JSON.stringify(data));
  };

  const getAndStorePurchasePreviewInfo = async (
    props: PurchasePreviewInfoProps,
  ): Promise<{ data?: any; status?: number }> => {
    const { currentCartItems, products, shippingAddress, inputPromoCode } =
      props;
    setPurchasePreviewErrors([]);
    const hasAddress = !!(
      shippingAddress?.postalCode && shippingAddress?.street1
    );
    const previewOrderData: PreviewOrderData = {
      subscriptions: getSubscriptionItems(
        currentCartItems,
        products,
        hasAddress ? shippingAddress.country : null,
        false,
        expeditedShippingCost > 0 ? 'expedited' : undefined,
      ),
      lineItems: [],
      currency: 'USD',
      includeShippingCosts: true,
      shipping: {},
    };

    const isDomestic = shippingAddress?.country?.toLowerCase() === 'us';

    // -----------------Address Data-----------------

    if (shippingAddress.address_id) {
      previewOrderData.shipping = { address_id: shippingAddress.address_id };
    } else if (shippingAddress.postalCode) {
      previewOrderData.shipping = { address: shippingAddress };
    }

    if (expeditedShippingCost > 0) {
      previewOrderData.shipping = {
        ...previewOrderData.shipping,
        shipping_method: 'expedited',
      };
    }

    // -----------------Address Data-----------------

    // -----------------Promo Code-----------------

    previewOrderData.couponCodes = [];
    if (inputPromoCode?.length) {
      previewOrderData.couponCodes.push(inputPromoCode);
    } else {
      // covering scenario where user intent coupon was preserved and bundle coupon is being removed
      const persistedUserEnteredPromo = getPersistedPromoCode();
      if (persistedUserEnteredPromo) {
        previewOrderData.couponCodes.push(persistedUserEnteredPromo);
      }
    }

    // -----------------Promo Code-----------------

    const previewPayload = await apiPurchasePreview(previewOrderData);
    if (previewPayload.status === 200) {
      const { chargeInvoice, shipping_costs } = previewPayload.data;

      let taxes = isDomestic
        ? chargeInvoice.tax
        : shipping_costs.tax_duty_amount || 0;
      let shippingCosts = isDomestic
        ? shipping_costs.total
        : shipping_costs.shipping_only_amount || 0;
      let previewInfo = {
        invoiceLineItems: chargeInvoice.lineItems.data,
        previewTotalPrice: chargeInvoice.balance,
        previewSubTotalPrice: chargeInvoice.subtotal - shippingCosts - taxes,
        previewCreditsApplied: chargeInvoice.paid,
        previewDiscountPrice: chargeInvoice.discount,
        // for use on PDPs. we don't want to show the user their credits applied - just the promo discount
        previewPdpPrice:
          chargeInvoice.subtotal -
          shippingCosts -
          taxes -
          chargeInvoice.discount,
        taxes: taxes,
        shippingCosts: shippingCosts,
        requestHash: JSON.stringify(previewOrderData),
        currency: previewOrderData.currency,
      };

      setPurchasePreviewInfo(previewInfo);
      return { data: previewPayload.data, status: previewPayload.status };
    }

    const errorData = previewPayload.data;
    const errorCode = errorData.errorCode || 'UNKNOWN';

    if (errorCode === 'ITEM_NOT_AVAILABLE') {
      localStorage?.removeItem('seed_cart_items');
    }

    // sometimes users have an expired coupon trapped in their localstorage and it causes checkout errors
    // if purchase preview fails because of this we need to remove it from localstorage
    if (errorCode === 'INVALID_COUPON') {
      const localStorageCoupon = getPersistedPromoCode();
      if (previewOrderData.couponCodes.includes(localStorageCoupon)) {
        removePersistedPromoCode();
      }
    }

    setPurchasePreviewErrors([getErrorMessage(errorCode) || errorData.msg]);

    trackEvent('Checkout Step Failed', {
      checkoutStep: 1,
      checkout_id: getCheckoutId(),
      coupon_id: promoCode,
      failedAddressValidation: errorCode === 'BILLING_VALIDATION_ERROR',
      action: 'Purchase Preview',
      status_code: previewPayload.status,
      error_code: errorCode,
      message: errorData.msg,
    });
    return { data: previewPayload.data, status: previewPayload.status };
  };

  useEffect(() => {
    if (
      shippingAddress.country?.toLowerCase() !== 'us' &&
      expeditedShippingCost > 0
    ) {
      setExpeditedShippingCost(0);
    }
  }, [shippingAddress.country]);

  // ---------------- PurchasePreviewInfo ----------------
  const data = useMemo(
    () => ({
      purchasePreviewInfo,
      setPurchasePreviewInfo,
      getAndStorePurchasePreviewInfo,

      checkoutStep,
      setCheckoutStep,

      shippingAddress,
      setShippingAddress,
      isUsingNewShippingAddress,
      setIsUsingNewShippingAddress,
      shippingAddressErrors,
      setShippingAddressErrors,
      shippingAddressFormErrors,
      setShippingAddressFormErrors,
      clearShippingAddress,
      isEditingShippingAddress,
      setIsEditingShippingAddress,
      expeditedShippingCost,
      setExpeditedShippingCost,

      billingAddress,
      setBillingAddress,
      useShippingAsBillingAddress,
      setUseShippingAsBillingAddress,

      hasAcceptedTerms,
      setHasAcceptedTerms,

      hasOptedInToEmail,
      setHasOptedInToEmail,

      isSmsOptedIn,
      setIsSmsOptedIn,

      paymentType,
      setPaymentType,
      paymentToken,
      setPaymentToken,
      isUsingExistingPaymentMethod,
      setisUsingExistingPaymentMethod,

      userIsVerified,
      setUserIsVerified,

      termsError,
      setTermsError,
      billingAddressFormErrors,
      setBillingAddressFormErrors,
      billingAddressErrors,
      setBillingAddressErrors,
      paymentErrors,
      setPaymentErrors,
      paymentFormErrors,
      setPaymentFormErrors,
      submitErrors,
      setSubmitErrors,
      turnstileErrors,
      setTurnstileErrors,
      applePayErrors,
      setApplePayErrors,
      purchasePreviewErrors,
      setPurchasePreviewErrors,
      clearAllErrors,

      // Add coupon analytics methods
      trackCouponApplied,
      trackCouponDenied,
      trackCouponFraud,
      trackCouponRemoved,
    }),
    [
      purchasePreviewInfo,
      checkoutStep,
      paymentToken,
      paymentType,
      shippingAddress,
      isUsingNewShippingAddress,
      isEditingShippingAddress,
      expeditedShippingCost,
      setExpeditedShippingCost,
      billingAddress,
      useShippingAsBillingAddress,
      hasAcceptedTerms,
      hasOptedInToEmail,
      isSmsOptedIn,
      shippingAddressErrors,
      shippingAddressFormErrors,
      billingAddressErrors,
      paymentErrors,
      termsError,
      submitErrors,
      turnstileErrors,
      purchasePreviewErrors,
      applePayErrors,
      setApplePayErrors,
    ],
  );

  return (
    // @ts-expect-error
    <CheckoutContext.Provider value={data}>{children}</CheckoutContext.Provider>
  );
};
